import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { testimonials } from '@/mocks/anime';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('loading');
    setFormError('');
    try {
      const formData = new FormData(form);
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          body.append(key, value as string);
        }
      });
      const res = await fetch('https://readdy.ai/api/form/d9p0edaftmtnfcfh0f80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const stats = [
    { label: '动漫资源', value: '1000+' },
    { label: '动漫分类', value: '6' },
    { label: '月活跃用户', value: '50万+' },
    { label: '累计播放', value: '1亿+' },
  ];

  const contactFaq = [
    {
      question: '樱花动漫网收费吗？',
      answer: '樱花动漫网完全免费，无需注册或订阅即可在线观看所有高清动漫内容，我们致力于为动漫爱好者提供最便捷的观看体验。',
    },
    {
      question: '支持哪些设备观看？',
      answer: '樱花动漫网支持所有主流设备，包括电脑、平板和手机，自适应各种屏幕尺寸，让您随时随地观看动漫。',
    },
    {
      question: '动漫资源多久更新一次？',
      answer: '我们的动漫资源持续更新，热门连载动漫每周同步更新，确保您能第一时间观看到最新的剧集内容。',
    },
    {
      question: '如何向樱花动漫网提供建议？',
      answer: '您可以通过本页的联系表单、发送邮件至 contact@sakura-anime.com，或在社交媒体上与我们联系，我们非常重视每一位用户的反馈。',
    },
  ];

  const faqSchemaData = {
    mainEntity: contactFaq.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <title>关于我们 - 樱花动漫网 品牌介绍 联系方式</title>
      <meta name="description" content="樱花动漫网是一个专注于动漫的网站，让您在线观看高清动漫，无需注册或订阅。了解我们的品牌故事、用户口碑和联系方式，加入樱花动漫的大家庭。" />
      <meta name="keywords" content="樱花动漫,关于樱花动漫,动漫网站介绍,樱花动漫联系方式,动漫平台" />
      <link rel="canonical" href="https://www.sakura-anime.com/about" />
      <meta property="og:title" content="关于我们 - 樱花动漫网" />
      <meta property="og:description" content="樱花动漫网 - 专注动漫的在线观看平台，了解我们的品牌故事和联系方式。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.sakura-anime.com/about" />
      <meta property="og:site_name" content="樱花动漫" />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="关于我们 - 樱花动漫网" />
      <meta name="twitter:description" content="樱花动漫网 - 专注动漫的在线观看平台，了解我们的品牌故事和联系方式。" />

      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={{
        url: 'https://www.sakura-anime.com/about',
        name: '关于我们 - 樱花动漫',
        description: '樱花动漫网品牌介绍、用户口碑和联系方式。',
      }} />
      <SchemaOrg type="FAQPage" data={faqSchemaData} />

      <ScrollToTop />
      <Navbar />

      <main>
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]} />
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于<span className="text-primary-600">樱花动漫</span>
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                我们是一群热爱动漫的创作者，致力于为所有动漫爱好者打造一个纯净、高质量的在线观看平台。
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <p className="text-2xl md:text-3xl font-bold text-primary-600 mb-1">{stat.value}</p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
                <div>
                  <img
                    src="https://readdy.ai/api/search-image?query=Anime%20style%20illustration%20of%20a%20cozy%20creative%20studio%20with%20anime%20posters%20on%20walls%2C%20warm%20lighting%2C%20people%20working%20on%20laptops%2C%20colorful%20and%20inspiring%20atmosphere%2C%20clean%20anime%20art%20style&width=800&height=500&seq=sakura-about-brand&orientation=landscape"
                    alt="樱花动漫团队"
                    title="樱花动漫 品牌故事"
                    className="w-full h-64 md:h-80 object-cover rounded-xl"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    我们的故事
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    樱花动漫网成立于2023年，由一群热爱动漫的年轻人创立。我们深知动漫爱好者对于高清画质和流畅体验的追求，因此我们从一开始就确定了"免费、高清、丰富"的核心定位。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    两年来，我们从最初的几十部动漫资源，发展到如今拥有超过1000部高清动漫的海量内容库。我们覆盖了热血战斗、奇幻冒险、恋爱日常、搞笑喜剧、悬疑推理、科幻未来等六大分类，满足不同动漫爱好者的需求。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们的使命是让每一个动漫爱好者都能轻松找到自己想看的内容，无需繁琐的注册流程，无需付费订阅，打开即看，随时随地沉浸在精彩的动漫世界中。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-600 text-sm">听听动漫爱好者们怎么说</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-2">
                    {Array.from({ length: 5 }, (_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  有任何问题、建议或合作意向，欢迎通过以下方式联系我们，我们会在24小时内回复。
                </p>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</p>
                      <p className="text-sm text-foreground-500">contact@sakura-anime.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-global-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">官方网站</p>
                      <p className="text-sm text-foreground-500">www.sakura-anime.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-1">工作时间</p>
                      <p className="text-sm text-foreground-500">周一至周日 9:00 - 22:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    发送消息
                  </h3>
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          type="text"
                          id="about-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          type="email"
                          id="about-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                          placeholder="请输入您的邮箱地址"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          消息内容
                        </label>
                        <textarea
                          id="about-message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-white focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                          placeholder="请输入您想说的内容（最多500字）"
                        ></textarea>
                      </div>
                      <div className="honeypot-field">
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                      >
                        {formStatus === 'loading' ? '发送中...' : '发送消息'}
                      </button>
                      {formStatus === 'success' && (
                        <p className="text-sm text-secondary-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500 text-center">{formError}</p>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                常见问题
              </h2>
            </div>
            <div className="max-w-3xl space-y-3">
              {contactFaq.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 border border-background-200/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-900">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}